import { useState, useEffect, useCallback } from "react";

// Individual firework particle
const Particle = ({ x, y, color, size, angle, speed, delay }) => {
  const [style, setStyle] = useState({
    left: x,
    top: y,
    opacity: 1,
    transform: 'scale(1)',
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      const distance = speed * 100;
      const endX = x + Math.cos(angle) * distance;
      const endY = y + Math.sin(angle) * distance + 50; // Add gravity effect
      
      setStyle({
        left: endX,
        top: endY,
        opacity: 0,
        transform: 'scale(0)',
      });
    }, delay);

    return () => clearTimeout(timer);
  }, [x, y, angle, speed, delay]);

  return (
    <div
      className="absolute rounded-full pointer-events-none transition-all duration-1000 ease-out"
      style={{
        ...style,
        width: size,
        height: size,
        backgroundColor: color,
        boxShadow: `0 0 ${size * 2}px ${color}, 0 0 ${size * 4}px ${color}`,
      }}
    />
  );
};

// Single firework burst
const FireworkBurst = ({ x, y, color, particleCount = 12 }) => {
  const particles = [];
  const colors = [color, '#fff', color];
  
  for (let i = 0; i < particleCount; i++) {
    const angle = (i / particleCount) * Math.PI * 2;
    const speed = 0.5 + Math.random() * 1;
    const size = 4 + Math.random() * 6;
    const particleColor = colors[Math.floor(Math.random() * colors.length)];
    
    particles.push(
      <Particle
        key={i}
        x={x}
        y={y}
        color={particleColor}
        size={size}
        angle={angle}
        speed={speed}
        delay={Math.random() * 100}
      />
    );
  }

  // Add trailing particles
  for (let i = 0; i < 8; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 0.3 + Math.random() * 0.5;
    const size = 2 + Math.random() * 3;
    
    particles.push(
      <Particle
        key={`trail-${i}`}
        x={x}
        y={y}
        color={color}
        size={size}
        angle={angle}
        speed={speed}
        delay={100 + Math.random() * 200}
      />
    );
  }

  return <>{particles}</>;
};

// Sparkle effect
const Sparkle = ({ x, y, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const [style, setStyle] = useState({});

  useEffect(() => {
    const showTimer = setTimeout(() => {
      setVisible(true);
      setStyle({
        left: x,
        top: y,
        transform: 'scale(1) rotate(0deg)',
        opacity: 1,
      });
    }, delay);

    const hideTimer = setTimeout(() => {
      setStyle(prev => ({
        ...prev,
        transform: 'scale(0) rotate(180deg)',
        opacity: 0,
      }));
    }, delay + 500);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [x, y, delay]);

  if (!visible) return null;

  return (
    <div
      className="absolute pointer-events-none transition-all duration-500 ease-out"
      style={style}
    >
      <svg width="20" height="20" viewBox="0 0 20 20" className="text-yellow-300">
        <path
          fill="currentColor"
          d="M10 0l2.5 7.5L20 10l-7.5 2.5L10 20l-2.5-7.5L0 10l7.5-2.5z"
        />
      </svg>
    </div>
  );
};

// Main Fireworks component
export function Fireworks({ active, duration = 3000, intensity = 'high' }) {
  const [bursts, setBursts] = useState([]);
  const [sparkles, setSparkles] = useState([]);

  const colors = [
    '#ff6b6b', // Red
    '#ffd93d', // Yellow  
    '#6bcb77', // Green
    '#4d96ff', // Blue
    '#ff9f43', // Orange
    '#a55eea', // Purple
    '#fd79a8', // Pink
    '#00cec9', // Cyan
  ];

  const createBurst = useCallback(() => {
    const x = 10 + Math.random() * 80; // 10-90% of width
    const y = 10 + Math.random() * 60; // 10-70% of height
    const color = colors[Math.floor(Math.random() * colors.length)];
    const particleCount = intensity === 'high' ? 16 : intensity === 'medium' ? 12 : 8;
    
    return {
      id: Date.now() + Math.random(),
      x: `${x}%`,
      y: `${y}%`,
      color,
      particleCount,
    };
  }, [intensity]);

  const createSparkle = useCallback(() => {
    return {
      id: Date.now() + Math.random(),
      x: `${5 + Math.random() * 90}%`,
      y: `${5 + Math.random() * 90}%`,
      delay: Math.random() * 500,
    };
  }, []);

  useEffect(() => {
    if (!active) {
      setBursts([]);
      setSparkles([]);
      return;
    }

    // Initial bursts
    const initialBursts = [];
    const burstCount = intensity === 'high' ? 5 : intensity === 'medium' ? 3 : 2;
    for (let i = 0; i < burstCount; i++) {
      initialBursts.push(createBurst());
    }
    setBursts(initialBursts);

    // Initial sparkles
    const initialSparkles = [];
    for (let i = 0; i < 10; i++) {
      initialSparkles.push(createSparkle());
    }
    setSparkles(initialSparkles);

    // Continue adding bursts
    const burstInterval = setInterval(() => {
      setBursts(prev => {
        const newBursts = [...prev, createBurst()];
        // Keep only last 10 bursts for performance
        return newBursts.slice(-10);
      });
    }, intensity === 'high' ? 300 : intensity === 'medium' ? 500 : 800);

    // Continue adding sparkles
    const sparkleInterval = setInterval(() => {
      setSparkles(prev => {
        const newSparkles = [...prev, createSparkle()];
        return newSparkles.slice(-15);
      });
    }, 200);

    // Stop after duration
    const stopTimer = setTimeout(() => {
      clearInterval(burstInterval);
      clearInterval(sparkleInterval);
    }, duration);

    return () => {
      clearInterval(burstInterval);
      clearInterval(sparkleInterval);
      clearTimeout(stopTimer);
    };
  }, [active, duration, intensity, createBurst, createSparkle]);

  if (!active && bursts.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {/* Gradient overlay for atmosphere */}
      <div 
        className="absolute inset-0 transition-opacity duration-500"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(255,215,0,0.1) 0%, transparent 70%)',
          opacity: active ? 1 : 0,
        }}
      />
      
      {/* Firework bursts */}
      {bursts.map(burst => (
        <div
          key={burst.id}
          className="absolute"
          style={{ left: burst.x, top: burst.y }}
        >
          <FireworkBurst
            x={0}
            y={0}
            color={burst.color}
            particleCount={burst.particleCount}
          />
        </div>
      ))}

      {/* Sparkles */}
      {sparkles.map(sparkle => (
        <Sparkle
          key={sparkle.id}
          x={sparkle.x}
          y={sparkle.y}
          delay={sparkle.delay}
        />
      ))}

      {/* Confetti-like elements */}
      {active && (
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={`confetti-${i}`}
              className="absolute w-2 h-2 rounded-full animate-confetti-fall"
              style={{
                left: `${Math.random() * 100}%`,
                backgroundColor: colors[i % colors.length],
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Simple level up celebration effect
export function LevelUpCelebration({ show, level }) {
  if (!show) return null;

  return (
    <>
      <Fireworks active={show} duration={4000} intensity="high" />
      
      {/* Center level announcement */}
      <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
        <div className="animate-level-up-pop text-center">
          <div className="text-6xl mb-2">🎉</div>
          <div className="font-display text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 animate-pulse">
            LEVEL {level}!
          </div>
          <div className="text-xl text-white/80 font-body mt-2">
            Congratulations!
          </div>
        </div>
      </div>
    </>
  );
}

export default Fireworks;
